import type { IUserID } from '@bridebook/models/source/models/Users.types';
import {
  default as jsonwebtoken,
  type Algorithm,
  type JwtPayload,
  type SignOptions,
} from 'jsonwebtoken';
import type { StringValue } from 'ms';

type DecodingKeys = Pick<JwtPayload, 'aud' | 'exp' | 'iat' | 'iss' | 'sub'>;
type EncodingKeys = Pick<SignOptions, 'algorithm' | 'audience' | 'issuer' | 'subject'>;

export class JWT {
  #options: EncodingKeys = {
    algorithm: 'HS256',
    audience: ['bridebook.com'],
    issuer: 'bridebook.com',
  };

  constructor(
    private readonly secret: string,
    algorithm: Extract<Algorithm, `HS${string}`> = 'HS256',
  ) {
    this.#options.algorithm = algorithm;
  }

  /**
   * Determines if a token is a Firebase custom token (without verifying it) by checking key claims.
   * Firebase custom tokens have a specific audience value and a subject that represents the user ID.
   */
  static isCustomToken(token: unknown) {
    if (typeof token !== 'string') {
      return false;
    }

    const claims = {
      aud: 'https://identitytoolkit.googleapis.com/google.identity.identitytoolkit.v1.IdentityToolkit',
      iss: /^[^@]+@[^.]+[.]iam[.]gserviceaccount[.]com$/,
      sub: /^[^@]+@[^.]+[.]iam[.]gserviceaccount[.]com$/,
      uid: /^\S{1,128}$/,
    };

    try {
      const decoded = jsonwebtoken.decode(token, { json: true }) ?? {};

      for (const [claim, expected] of Object.entries(claims)) {
        const value = decoded[claim as keyof typeof decoded];

        if (typeof expected === 'string') {
          if (value !== expected) {
            return false;
          }
        } else if (expected instanceof RegExp) {
          if (typeof value !== 'string' || expected.test(value) !== true) {
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Determines if a token is a Firebase identity token (without verifying it) by checking key claims.
   * Firebase identity tokens have a specific issuer value and a subject that represents the user ID.
   */
  static isIdentityToken(token: unknown) {
    if (typeof token !== 'string') {
      return false;
    }

    const claims = {
      iss: /^https:[/][/]securetoken[.]google[.]com[/][^/]+$/,
      sub: /^\S{1,128}$/,
      user_id: /^\S{1,128}$/,
    };

    try {
      const decoded = jsonwebtoken.decode(token, { json: true }) ?? {};

      for (const [claim, expected] of Object.entries(claims)) {
        const value = decoded[claim as keyof typeof decoded];

        if (typeof expected === 'string') {
          if (value !== expected) {
            return false;
          }
        } else if (expected instanceof RegExp) {
          if (typeof value !== 'string' || expected.test(value) !== true) {
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Set the audience claim for the JWT, which specifies the intended recipients of the token.
   */
  set audience(audience: string[]) {
    this.#options.audience = audience;
  }

  /**
   * Set the issuer claim for the JWT, which identifies the principal that issued the token.
   */
  set issuer(issuer: string) {
    this.#options.issuer = issuer;
  }

  /**
   * Set the subject claim for the JWT, which typically represents the user ID or principal.
   */
  set subject(subject: IUserID) {
    this.#options.subject = subject;
  }

  /**
   * Signs a payload into a JWT token string with the configured secret and options.
   * Optionally accepts an expiration time.
   *
   * @param payload - The payload to include in the JWT.
   * @param expiration - Optional expiration time for the token (e.g., '1h', '30m', or a numeric value in seconds).
   */
  sign<T extends Record<string, unknown>>(
    payload?: T & Partial<Record<keyof SignOptions, never>>,
    expiration?: StringValue | number,
  ) {
    if (expiration != null) {
      return jsonwebtoken.sign({ ...payload }, this.secret, {
        ...this.#options,
        expiresIn: expiration,
      });
    }

    return jsonwebtoken.sign({ ...payload }, this.secret, this.#options);
  }

  /**
   * Sets the subject claim for the JWT, which typically represents the user ID.
   *
   * @param id - The user ID to set as the subject of the JWT.
   */
  user(id: IUserID) {
    const clone = new JWT(
      this.secret,
      this.#options.algorithm as Extract<Algorithm, `HS${string}`>,
    );

    clone.subject = id;

    return clone;
  }

  /**
   * Verifies a JWT token and returns the decoded payload if valid.
   * Throws an error if the token is invalid or expired.
   */
  verify<T extends Record<string, unknown>>(token: string) {
    return jsonwebtoken.verify(token, this.secret, this.#options) as T & DecodingKeys;
  }
}
